import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getManualLog, handleRequestManualLog, acceptAllPendingManualLogs, findDuplicateManualLogs } from '@/api/manualLog';
import { ManualLogStatus } from '@/models/manualLog';
import { startOfMonth, endOfMonth, format } from 'date-fns';
import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

const DEFAULT_LIMIT = 10;

type ManualLogFilters = {
    startDate: string;
    endDate: string;
    page: number;
    limit: number;
    status: ManualLogStatus | 'all';
    uid: string;
};

const useManualLog = () => {
    const queryClient = useQueryClient();
    const [searchParams, setSearchParams] = useSearchParams();

    const [filters, setFilters] = useState<ManualLogFilters>(() => {
        const params = Object.fromEntries(searchParams.entries());
        return {
            startDate:
                params.startDate ||
                format(startOfMonth(new Date()), 'yyyy-MM-dd'),
            endDate:
                params.endDate || format(endOfMonth(new Date()), 'yyyy-MM-dd'),
            page: Number(params.page) || 1,
            limit: Number(params.limit) || DEFAULT_LIMIT,
            status: (params.status as ManualLogStatus) || 'all',
            uid: params.uid || 'all',
        };
    });

    useEffect(() => {
        const params = new URLSearchParams();
        Object.entries(filters).forEach(([key, value]) => {
            if (value && value !== 'all') {
                params.set(key, value.toString());
            }
        });
        setSearchParams(params);
    }, [filters, setSearchParams]);

    const { data: manualLogs, isLoading: isLoadingManualLogs } = useQuery({
        queryKey: ['manualLogs', filters],
        queryFn: () => getManualLog(filters),
    });

    const updateManualLogMutation = useMutation({
        mutationFn: handleRequestManualLog,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['manualLogs'] });
        },
    });

    const handleUpdateStatus = (
        manualLogId: string,
        status: ManualLogStatus
    ) => {
        updateManualLogMutation.mutate({ manualLogId, status });
    };

    const acceptAllPendingMutation = useMutation({
        mutationFn: acceptAllPendingManualLogs,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['manualLogs'] });
        },
    });

    const handleAcceptAllPending = (startDate?: string, endDate?: string) => {
        return acceptAllPendingMutation.mutateAsync({ startDate, endDate });
    };

    const findDuplicatesMutation = useMutation({
        mutationFn: findDuplicateManualLogs,
    });

    const handleFindDuplicates = (startDate?: string, endDate?: string) => {
        return findDuplicatesMutation.mutateAsync({ startDate, endDate });
    };

    const updateFiltersWithReset = (newFilters: Partial<ManualLogFilters>) => {
        setFilters((prev) => {
            const updatedFilters = { ...prev, ...newFilters };

            // Reset page to 1 if status or date filters change
            if (
                newFilters.status !== undefined ||
                newFilters.startDate !== undefined ||
                newFilters.endDate !== undefined
            ) {
                updatedFilters.page = 1;
            }

            return updatedFilters;
        });
    };

    return {
        manualLogs,
        isLoadingManualLogs,
        handleUpdateStatus,
        handleAcceptAllPending,
        isAcceptingAll: acceptAllPendingMutation.isPending,
        handleFindDuplicates,
        isFindingDuplicates: findDuplicatesMutation.isPending,
        filters,
        setFilters,
        updateFiltersWithReset,
        DEFAULT_LIMIT,
    };
};

export default useManualLog;
